import { useState, useEffect } from 'react';
import { Button, Typography, Snackbar, Paper, Stack } from '@mui/material';
import { useRouterState } from '@tanstack/react-router';
import { usePostHog, CONSENT_KEY } from './PostHogProvider';

type ConsentStatus = 'pending' | 'accepted' | 'declined';

function getStoredConsent(): ConsentStatus {
  if (typeof window === 'undefined') return 'pending';
  return (localStorage.getItem(CONSENT_KEY) as ConsentStatus) || 'pending';
}

export function CookieConsent() {
  const [open, setOpen] = useState(false);
  const { optIn, optOut } = usePostHog();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Don't show cookie consent on /contact (Chatwoot page)
  const isContactPage = pathname === '/contact';

  useEffect(() => {
    if (isContactPage) return;

    const consent = getStoredConsent();
    if (consent === 'pending') {
      // Show immediately. A delay (previously 1.5s) means visitors who bounce
      // before the banner renders never see it, never opt in, and produce zero
      // captured events — the www marketing blind spot in IDEA-269.
      setOpen(true);
    } else if (consent === 'accepted') {
      optIn();
    } else {
      optOut();
    }
  }, [isContactPage]);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    optIn();
    setOpen(false);
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    optOut();
    setOpen(false);
  };

  // Don't render anything on /contact page
  if (isContactPage) return null;

  return (
    <Snackbar
      open={open}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ maxWidth: 600 }}
    >
      <Paper
        elevation={8}
        sx={{
          p: 3,
          borderRadius: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" component="p" sx={{ mb: 1 }}>
          We value your privacy
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          We use cookies to analyse how you use our site and improve your experience.
          By clicking "Accept", you agree to our use of analytics cookies.
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" color="inherit" onClick={handleDecline}>
            Decline
          </Button>
          <Button variant="contained" onClick={handleAccept}>
            Accept
          </Button>
        </Stack>
      </Paper>
    </Snackbar>
  );
}
